import type { Context, ContextCallback, ContextType } from '@lit/context'
import type { ReactiveController, ReactiveControllerHost } from 'lit'
import { ContextEvent } from '@lit/context'

type ContextHost = ReactiveControllerHost & HTMLElement

class InputGroupContextProviderEvent<C extends Context<unknown, unknown>> extends Event {
  constructor(
    readonly context: C,
    readonly contextTarget: Element,
  ) {
    super('context-provider', { bubbles: true, composed: true })
  }
}

/**
 * A Context-protocol provider whose value is only exposed to the single
 * accepted direct text control. Other descendants receive `undefined` and the
 * request stops here, preventing either this group or an enclosing Field from
 * leaking presentation state into controls composed inside addons.
 */
export class InputGroupContextProvider<C extends Context<unknown, unknown>> implements ReactiveController {
  private value: ContextType<C>
  private subscriptions = new Map<ContextCallback<ContextType<C>>, {
    target: Element
    unsubscribe: () => void
  }>()

  constructor(
    private host: ContextHost,
    private context: C,
    initialValue: ContextType<C>,
    private accepts: (target: Element) => boolean,
  ) {
    this.value = initialValue
    host.addEventListener('context-request', this.handleContextRequest)
    host.addEventListener('context-provider', this.handleProviderRequest)
    host.addController(this)
  }

  hostConnected() {
    this.host.dispatchEvent(new InputGroupContextProviderEvent(this.context, this.host))
  }

  setValue(value: ContextType<C>) {
    this.value = value
    for (const [callback, subscription] of this.subscriptions) {
      callback(
        this.accepts(subscription.target) ? value : undefined as ContextType<C>,
        subscription.unsubscribe,
      )
    }
  }

  private handleContextRequest = (event: ContextEvent<Context<unknown, unknown>>) => {
    if (event.context !== this.context) {
      return
    }

    const target = event.contextTarget ?? event.composedPath()[0]
    if (!(target instanceof Element) || target === this.host) {
      return
    }

    event.stopPropagation()
    const callback = event.callback as ContextCallback<ContextType<C>>
    const previousSubscription = this.subscriptions.get(callback)
    const subscription = {
      target,
      unsubscribe: () => {
        if (this.subscriptions.get(callback) === subscription) {
          this.subscriptions.delete(callback)
        }
      },
    }
    if (event.subscribe) {
      this.subscriptions.set(callback, subscription)
      previousSubscription?.unsubscribe()
    }
    callback(
      this.accepts(target) ? this.value : undefined as ContextType<C>,
      event.subscribe ? subscription.unsubscribe : undefined,
    )
  }

  private handleProviderRequest = (event: HTMLElementEventMap['context-provider']) => {
    if (event.context !== this.context || event.contextTarget === this.host) {
      return
    }

    for (const [callback, subscription] of [...this.subscriptions]) {
      subscription.target.dispatchEvent(
        new ContextEvent(this.context, subscription.target, callback, true),
      )
    }
    event.stopPropagation()
  }
}
